import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Phone, Mail, MapPin, Clock, CheckCircle, Calendar, Send } from "lucide-react";
import axios from "axios";
import HCaptchaField from "../components/HCaptchaField";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const devices = ["iPhone", "MacBook Air", "MacBook Pro", "iMac", "iPad", "Apple Watch", "Autre"];

export default function ContactPage() {
  useEffect(() => { document.title = "Contact & RDV — Atelier du Mac Paris"; }, []);

  const [form, setForm] = useState({
    nom: "", email: "", telephone: "", appareil: "iPhone",
    modele: "", message: "", type_demande: "contact",
  });
  const [status, setStatus] = useState(null); // null | 'loading' | 'success' | 'error'
  const [activeTab, setActiveTab] = useState("rdv"); // rdv | contact
  const [captchaToken, setCaptchaToken] = useState("");
  const captchaRef = useRef(null);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!captchaToken) { setStatus("captcha"); return; }
    setStatus("loading");
    try {
      await axios.post(`${BACKEND_URL}/api/contact`, { ...form, type_demande: activeTab, hcaptcha_token: captchaToken });
      setStatus("success");
      setForm({ nom: "", email: "", telephone: "", appareil: "iPhone", modele: "", message: "", type_demande: activeTab });
      setCaptchaToken("");
      captchaRef.current?.resetCaptcha();
    } catch {
      setStatus("error");
      captchaRef.current?.resetCaptcha();
      setCaptchaToken("");
    }
  };

  return (
    <div className="bg-[#0A0A0C] min-h-screen">
      <section className="pt-32 pb-8 max-w-7xl mx-auto px-6">
        <Link to="/" className="inline-flex items-center gap-1.5 text-xs text-white/40 hover:text-white/60 mb-8 transition-colors">
          <ChevronRight className="w-3 h-3 rotate-180" />
          Retour à l'accueil
        </Link>
        <p className="text-xs font-bold uppercase tracking-widest text-[#FF5C00] mb-3">Contact & RDV</p>
        <h1 className="text-5xl sm:text-6xl font-black tracking-tighter text-white mb-4">
          Parlons de votre<br />réparation.
        </h1>
        <p className="text-lg text-white/60 max-w-xl">
          Prenez rendez-vous en ligne ou envoyez-nous un message. Réponse sous 24h.
        </p>
      </section>

      <section className="pb-24 max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-5 gap-12">
          {/* Form */}
          <div className="lg:col-span-3">
            {/* Tabs */}
            <div className="flex gap-2 mb-8 p-1 bg-[#121215] rounded-full w-fit">
              <button
                data-testid="tab-rdv"
                onClick={() => setActiveTab("rdv")}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-all ${
                  activeTab === "rdv" ? "bg-[#FF5C00] text-white" : "text-white/50 hover:text-white"
                }`}
              >
                <Calendar className="w-4 h-4" /> Prise de RDV
              </button>
              <button
                data-testid="tab-contact"
                onClick={() => setActiveTab("contact")}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-all ${
                  activeTab === "contact" ? "bg-[#FF5C00] text-white" : "text-white/50 hover:text-white"
                }`}
              >
                <Mail className="w-4 h-4" /> Message
              </button>
            </div>

            {status === "success" ? (
              <div
                data-testid="contact-success"
                className="p-8 rounded-2xl border border-[#34C759]/30 bg-[#34C759]/5 text-center"
              >
                <div className="w-16 h-16 rounded-full bg-[#34C759]/20 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-[#34C759]" />
                </div>
                <h3 className="text-xl font-black text-white mb-2">Demande envoyée !</h3>
                <p className="text-white/50 text-sm">
                  {activeTab === "rdv"
                    ? "Nous vous confirmerons votre rendez-vous par email sous 24h."
                    : "Nous vous répondrons dans les plus brefs délais."}
                </p>
                <button
                  onClick={() => setStatus(null)}
                  className="mt-6 text-sm text-[#FF5C00] hover:underline"
                >
                  Envoyer une autre demande
                </button>
              </div>
            ) : (
              <form
                data-testid="contact-form"
                onSubmit={handleSubmit}
                className="space-y-5"
              >
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-widest text-white/40 mb-2">
                      Nom complet *
                    </label>
                    <input
                      data-testid="input-nom"
                      type="text"
                      name="nom"
                      value={form.nom}
                      onChange={handleChange}
                      required
                      placeholder="Jean Dupont"
                      className="w-full bg-[#1A1A1E] border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-[#FF5C00] transition-colors text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-widest text-white/40 mb-2">
                      Email *
                    </label>
                    <input
                      data-testid="input-email"
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      required
                      placeholder="jean@email.com"
                      className="w-full bg-[#1A1A1E] border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-[#FF5C00] transition-colors text-sm"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-widest text-white/40 mb-2">
                      Téléphone
                    </label>
                    <input
                      data-testid="input-telephone"
                      type="tel"
                      name="telephone"
                      value={form.telephone}
                      onChange={handleChange}
                      placeholder="06 12 34 56 78"
                      className="w-full bg-[#1A1A1E] border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-[#FF5C00] transition-colors text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-widest text-white/40 mb-2">
                      Appareil *
                    </label>
                    <select
                      data-testid="input-appareil"
                      name="appareil"
                      value={form.appareil}
                      onChange={handleChange}
                      required
                      className="w-full bg-[#1A1A1E] border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#FF5C00] transition-colors text-sm"
                    >
                      {devices.map((d) => (
                        <option key={d} value={d} style={{ background: "#1A1A1E" }}>{d}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest text-white/40 mb-2">
                    Modèle précis
                  </label>
                  <input
                    data-testid="input-modele"
                    type="text"
                    name="modele"
                    value={form.modele}
                    onChange={handleChange}
                    placeholder="Ex: iPhone 15 Pro Max, MacBook Air M2..."
                    className="w-full bg-[#1A1A1E] border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-[#FF5C00] transition-colors text-sm"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest text-white/40 mb-2">
                    {activeTab === "rdv" ? "Description du problème *" : "Message *"}
                  </label>
                  <textarea
                    data-testid="input-message"
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    required
                    rows={4}
                    placeholder={
                      activeTab === "rdv"
                        ? "Décrivez le problème : écran cassé, batterie faible, ne s'allume plus..."
                        : "Votre message..."
                    }
                    className="w-full bg-[#1A1A1E] border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-[#FF5C00] transition-colors text-sm resize-none"
                  />
                </div>

                {status === "error" && (
                  <p data-testid="contact-error" className="text-sm text-red-400">
                    Une erreur s'est produite. Veuillez réessayer.
                  </p>
                )}
                {status === "captcha" && (
                  <p className="text-sm text-yellow-400">Veuillez compléter le CAPTCHA avant d'envoyer.</p>
                )}

                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest text-white/40 mb-2">Vérification anti-robot *</label>
                  <HCaptchaField captchaRef={captchaRef} onVerify={setCaptchaToken} onExpire={() => setCaptchaToken("")} />
                </div>

                <button
                  data-testid="contact-submit-btn"
                  type="submit"
                  disabled={status === "loading"}
                  className="w-full flex items-center justify-center gap-2 bg-[#FF5C00] hover:bg-[#E05200] disabled:opacity-60 text-white font-semibold py-4 rounded-full transition-all duration-200 hover:-translate-y-0.5"
                >
                  {status === "loading" ? (
                    <span className="animate-pulse">Envoi en cours...</span>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      {activeTab === "rdv" ? "Demander un RDV" : "Envoyer le message"}
                    </>
                  )}
                </button>
              </form>
            )}
          </div>

          {/* Info panel */}
          <div className="lg:col-span-2 space-y-6">
            <div className="p-6 rounded-2xl border border-white/5" style={{ background: "#121215" }}>
              <h3 className="text-sm font-bold uppercase tracking-widest text-white/30 mb-5">Nos ateliers</h3>
              <div className="space-y-5">
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-[#FF5C00] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-white">Paris 11e</p>
                    <p className="text-sm text-white/50">9 Rue de Mont-Louis, 75011 Paris</p>
                    <p className="text-xs text-white/30 mt-0.5">Métro Philippe Auguste (L2)</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-[#FF5C00] flex-shrink-0" />
                  <a href="tel:+33611367002" className="text-sm text-white/60 hover:text-white transition-colors">06 11 36 70 02</a>
                </div>
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-[#FF5C00] flex-shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-white">Lun–Sam : 11h–20h</p>
                    <p className="text-xs text-white/40">Sans rendez-vous</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-[#FF5C00] flex-shrink-0" />
                  <a href="mailto:contact@atelierdumac.fr" className="text-sm text-white/60 hover:text-white transition-colors">
                    contact@atelierdumac.fr
                  </a>
                </div>
              </div>
            </div>

            <div className="p-6 rounded-2xl border border-[#FF5C00]/20 bg-[#FF5C00]/5">
              <h3 className="text-sm font-bold text-white mb-3">Avant votre visite</h3>
              <ul className="space-y-2.5">
                {[
                  "Sauvegardez vos données",
                  "Notez votre code d'accès",
                  "Venez sans RDV, lun-sam 11h-20h",
                  "Diagnostic gratuit sur place",
                ].map((item) => (
                  <li key={item} className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-[#FF5C00] flex-shrink-0" />
                    <span className="text-sm text-white/70">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
